import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, FolderOpen, LayoutGrid, Users } from "lucide-react";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { useChargement } from "@/lib/chargement";
import { api, type Resultats, type Trouvaille } from "@/lib/api";

/**
 * La recherche globale du §4 : « numéro de commande, code chantier, client, adresse ».
 *
 * Quatre familles côte à côte, parce que celui qui tape « Dupont » ne sait pas
 * encore s'il cherche un client, une affaire, une commande ou un lot.
 * Chacune est filtrée par son propre périmètre, côté serveur.
 */
const FAMILLES = [
  { cle: "affaires", titre: "Affaires", vers: "/affaires/", icone: Briefcase },
  { cle: "commandes", titre: "Commandes", vers: "/commandes/", icone: FolderOpen },
  { cle: "lots", titre: "Lots", vers: "/lots/", icone: LayoutGrid },
  { cle: "clients", titre: "Clients", vers: "/clients/", icone: Users },
] as const;

export function PageRecherche() {
  const [parametres] = useSearchParams();
  const terme = parametres.get("q") ?? "";
  const { donnees, erreur, enCours, recharger } = useChargement<Resultats>(
    (signal) => api.recherche(terme, signal),
    [terme],
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Recherche</h1>
        <p className="mt-1 text-sm text-encre-douce">
          {terme ? <>Résultats pour « {terme} »</> : "Saisissez au moins deux caractères."}
        </p>
      </div>

      {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}
      {!erreur && enCours ? <Chargement message="Recherche en cours…" /> : null}

      {donnees && donnees.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucun résultat"
            detail="Rien dans votre périmètre ne correspond à cette recherche."
          />
        </Carte>
      ) : null}

      {donnees && donnees.total > 0
        ? FAMILLES.map((famille) => {
            const trouvailles = donnees[famille.cle] as Trouvaille[];
            if (trouvailles.length === 0) return null;
            const Icone = famille.icone;
            return (
              <Carte key={famille.cle}>
                <EnTeteCarte className="flex items-center gap-2">
                  <Icone className="h-4 w-4 text-encre-douce" aria-hidden />
                  <h2 className="text-sm font-semibold text-encre">
                    {famille.titre} ({trouvailles.length})
                  </h2>
                </EnTeteCarte>
                <CorpsCarte className="p-0">
                  <ul className="divide-y divide-filet">
                    {trouvailles.map((trouvaille) => (
                      <li key={trouvaille.id}>
                        <Link
                          to={`${famille.vers}${trouvaille.id}`}
                          className="flex flex-col px-4 py-3 hover:bg-chene-clair/25"
                        >
                          <span className="text-sm font-medium text-encre">{trouvaille.titre}</span>
                          {trouvaille.detail ? (
                            <span className="text-sm text-encre-douce">{trouvaille.detail}</span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CorpsCarte>
              </Carte>
            );
          })
        : null}
    </div>
  );
}
